import merge from 'lodash-es/merge';
import { logUsage } from 'owa-analytics';
import * as Schema from 'owa-graph-schema';
import { getGuid } from 'owa-guid';
import { isSuccessStatusCode } from 'owa-http-status-codes';
import { getAccessTokenforResourceAsLazy } from 'owa-tokenprovider';

type TeamsService = AppService | ServiceDiscovery;
type AppService = Schema.AppDefinition[];
type ServiceDiscovery = {
    region: string;
    partition: string;
    regionGtms: {
        appService: string;
        appServiceS2S: string;
        middleTier: string;
        middleTierS2S: string;
    };
};

export async function getMetaOSInstalledApps(): Promise<AppService> {
    return fetchInstalledApps();
}

async function teamsServiceFetch<Service extends TeamsService>(
    url: URL,
    optionsOverride: RequestInit = {}
): Promise<Service | null> {
    let [token, tokenPromise] = getAccessTokenforResourceAsLazy('https://teams.microsoft.com');
    if (!token) {
        token = await tokenPromise;
        if (!token) {
            logUsage('AppServiceFetch-Error', {
                step: 'Auth',
                error: 'Empty token',
                service: url.toString(),
            });
            return null;
        }
    }

    const options: RequestInit = merge(
        {
            method: 'GET',
            headers: {
                Authorization: 'Bearer ' + token,
                Accept: 'application/json',
                'x-ms-client-request-id': getGuid(),
                'x-ms-client-type': 'Outlook Web',
            },
        },
        optionsOverride
    );
    return fetch(url.toString(), options)
        .then(response => {
            if (response.ok && isSuccessStatusCode(response.status)) {
                const data = response.json();
                if (!data) {
                    logUsage('AppServiceFetch-Error', {
                        step: 'Response',
                        error: 'Response empty',
                        service: url.toString(),
                    });
                    return null;
                }
                return data;
            }
            return null;
        })
        .catch(err => {
            logUsage('AppServiceFetch-Error', {
                step: 'Request',
                error: `Request failed: ${JSON.stringify(err)}\nRequest options: ${options}`,
                service: url.toString(),
            });
            return null;
        });
}

async function fetchAppServiceUrl(): Promise<URL | null> {
    const SERVICE_DISCOVERY_BASE_URL = 'https://teams.microsoft.com/api/authsvc';
    const SERVICE_DISCOVERY_PATH_PROD = '/v1.0/users/region';

    const url = new URL(SERVICE_DISCOVERY_BASE_URL);
    url.pathname += SERVICE_DISCOVERY_PATH_PROD;

    return teamsServiceFetch<ServiceDiscovery>(url, { method: 'POST' }).then(servicesPerRegion => {
        if (servicesPerRegion) {
            return new URL(servicesPerRegion.regionGtms.appService);
        }
        return null;
    });
}

async function fetchInstalledApps(): Promise<Schema.AppDefinition[]> {
    const ENTITLEMENTS_API_PATH_PROD = '/beta/users/apps/entitlements';
    const url: URL | null = await fetchAppServiceUrl();
    if (!url) {
        return [];
    }

    url.pathname += ENTITLEMENTS_API_PATH_PROD;
    return teamsServiceFetch<AppService>(url).then(appDefinitions => {
        if (appDefinitions && appDefinitions.length > 0) {
            return appDefinitions;
        }
        return [];
    });
}
